<template>
  <div class="widgetParent">
    <div class="widgetHeader" v-if="flags.showIFrameHomeHeader">
      <span class="header">{{ $t("widgetApp.home.header") }}</span>
      <img src="../../assets/powered-by-titan-header.svg" />
    </div>
    <!--
      Both add-mailbox nudges (first-mailbox and "add more") now render in
      AppBanner's top banner slot, so their placement is consistent with the
      other app banners (MWDC-6350/6351). cpWidget no longer owns them.
    -->
    <div class="widget-grid-container">
      <!-- check `themeImage` filter under src/global/middleware.js -->
      <div
        :class="`${route.imgFilename} sections`"
        v-for="route in allRoutes"
        :key="route.name"
        @click="handleClickOnHomeScreenTiles(route)"
      >
        <a>
          <img :src="route.imgFilename | themeImage" class="commonImgClass" />
          <div
            class="external-tab"
            v-if="route.isBillingRoute && !flags.disableIFramePaymentRedirection"
          ></div>
          <div class="widget-section-name">{{ $t(route.name) }}</div>
          <!--
            Email-accounts tile only: "X of Y email accounts created"
            status, shown while the user still has licenses left to use.
            Themed primary color so it reads as the partner's accent.
          -->
          <div
            v-if="route.id === 'email_accounts' && emailAccountsCreatedStatus"
            class="widget-section-status"
          >
            {{
              $t("emailAccounts.nudge.tile.createdCount", {
                created: emailAccountsCreatedStatus.created,
                total: emailAccountsCreatedStatus.total,
              })
            }}
          </div>
          <div class="widget-section-description">
            {{ $t(route.description) }}
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as helpers from "@/helpers";
import views from "../../views";
import billingService from "@/services/billing/billing.service";
import {
  isEmailBackupEligible,
  isFeatureEnabledInCurrentPlan,
} from "@/helpers/plan-feature.helper";
import { PLAN_FEATURE_MAP } from "@/helpers/const";
import pendingAccountsService from "@/services/pending-accounts/pending-accounts.service";
import Logger from "@/helpers/logger";
import { trackTabClicked } from "@/telemetry/mailboxNudgeEvents";
export default {
  name: "cpWidget",
  data: function () {
    return {
      theme: null,
    };
  },
  props: {
    titanMaxEnabled: Boolean,
    partnerId: Number,
  },
  computed: {
    ...mapGetters([
      "flags",
      "currentPlanDetails",
      "locale",
      "currentDomainMailboxDetails",
      "isCurrentPlanFetched",
    ]),
    ...mapGetters({
      isBuyMoreAccountsAllowed: "mail/isBuyMoreAccountsAllowed",
      canUpgradePlan: "mail/canUpgradePlan",
      mailPlans: "mail/plans",
      availableLicenses: "mail/availableLicenses",
      hasMailboxes: "mail/hasMailboxes",
      currentProductDetails: "mail/currentProductDetails",
      skippedFirstMailbox: "mail/mailboxNudge/skippedFirstMailbox",
      domainName: "domain/domainName",
    }),
    /**
     * "X of Y email accounts created" status for the Email accounts tile.
     * Shown only while the user still has licenses left to spend (i.e.
     * mailboxes are yet to be created) — once all seats are used the
     * status is dropped. Returns null when the plan hasn't loaded or
     * there's nothing left to create.
     */
    emailAccountsCreatedStatus() {
      if (!this.isCurrentPlanFetched) return null;
      const counts = this.currentProductDetails.noOfAccounts || {};
      const total = counts.limit ?? counts.purchased ?? 0;
      const created = counts.active ?? (this.currentDomainMailboxDetails || []).length;
      if (!total || this.availableLicenses <= 0) return null;
      return { created, total };
    },
    allRoutes: function () {
      const newRoutes = this.routes.filter(route => route.visible);
      return newRoutes.filter(route => !route.isModal);
    },
    routes() {
      const _isNeoBrand = helpers.isNeoBrand();
      return [
        {
          id: "email_accounts",
          name: "menuItems.emailAccounts",
          to: "/email_accounts",
          imgFilename: "email-accounts",
          description: "menuItemsDescription.emailAccounts",
          visible: true,
        },
        {
          id: "email_forwarders",
          name: "menuItems.internalForwarding",
          to: "/internal-forward",
          imgFilename: "internal-forwarding",
          description: "menuItemsDescription.emailForwarders",
          visible: true,
        },
        {
          id: "catch_all_email",
          name: "menuItems.catchAllEmail",
          to: "/catch_all_email",
          imgFilename: "catch-all-email",
          description: "menuItemsDescription.catchAllEmail",
          visible: true,
        },
        {
          id: "import_email",
          name: "menuItems.importEmail",
          to: "/import-email",
          imgFilename: "import-email",
          description: "menuItemsDescription.importEmail",
          visible: true,
        },
        {
          id: "domain_activation",
          name: "menuItems.domainActivation",
          to: "/domain-verification",
          imgFilename: "domain-activation",
          description: "menuItemsDescription.domainActivation",
          visible: true,
        },
        {
          id: "email_reputation",
          name: "menuItems.emailReputation",
          to: "/email-reputation",
          imgFilename: "email-reputation",
          description: "menuItemsDescription.emailReputation",
          visible: true,
        },
        {
          id: "spam_filter_control",
          name: "menuItems.spamFilterControl",
          to: "/spam-filter-control",
          imgFilename: "spam-filter-control",
          description: "menuItemsDescription.spamFilterControl",
          visible: isFeatureEnabledInCurrentPlan(
            PLAN_FEATURE_MAP.SPAM_FILTER_CONTROL,
            helpers.PRODUCTS.MAIL_SUITE
          ),
        },
        {
          id: "download_apps",
          name: _isNeoBrand ? "menuItems.getMobileApp" : "menuItems.downloadMobileDesktopMail",
          to: "/device-download",
          imgFilename: "mobile",
          description: _isNeoBrand
            ? "menuItemsDescription.getMobileApp"
            : "menuItemsDescription.getMobileDesktopAppSubText",
          visible: true,
        },
        {
          id: "configuration_instruction",
          name: `menuItems.${
            _isNeoBrand ? "neoConfigurationInstruction" : "titanConfigurationInstruction"
          }`,
          to: "/configure-desktop",
          imgFilename: "desktop-icon",
          description: "menuItemsDescription.configureDesktopAndMobile",
          visible: true,
        },
        {
          id: "email_backup",
          name: "menuItems.emailBackup",
          to: "/email-backup",
          imgFilename: "email-backup",
          description: "menuItemsDescription.emailBackup",
          visible: isEmailBackupEligible(this.currentPlanDetails, this.mailPlans),
        },
        {
          action: "upgradePlan",
          isBillingRoute: true,
          id: "billing_and_upgrade",
          name: "menuItems.billingAndUpgrade",
          to: "/billing-and-upgrade",
          imgFilename: "upgrade",
          description: "menuItemsDescription.billingAndUpgrade",
          visible: !(
            this.flags.hideIFrameUpgradeSection ||
            (this.flags.hideIFrameUpgradeSectionOnHighestPlan && !this.canUpgradePlan)
          ),
        },
        {
          action: "buyMoreAccounts",
          isBillingRoute: true,
          id: "buy_email_account",
          name: "menuItems.buyEmailAccounts",
          to: "/buy-email-account",
          imgFilename: "buyMoreAccounts",
          description: "menuItemsDescription.buyEmailAccounts",
          // Same rule the nudge Buy CTAs gate on — see mail/canBuyMoreAccounts.
          visible: this.isBuyMoreAccountsAllowed,
        },
        {
          id: "titan_max",
          name: "menuItems.titan_max",
          to: "/addons/titan_max",
          imgFilename: "titan-max",
          description: "menuItemsDescription.titanMax",
          visible: this.titanMaxEnabled,
        },
        {
          action: "billing",
          isBillingRoute: true,
          id: "billing_and_subscription",
          name: "menuItems.billingAndSubscription",
          to: "/billing-and-subscription",
          imgFilename: "billing-subscription",
          description: "menuItemsDescription.billingAndSubscription",
          visible: !this.flags.hideIFrameBillingSection,
        },
        {
          name: "menuItems.createEmailAccountModal",
          to: "createEmailAccountModal",
          isModal: true,
        },
        {
          name: "menuItems.catchAllEmailModal",
          to: "catchAllEmailModal",
          isModal: true,
        },
        {
          name: "menuItems.manageAdminRightsModal",
          to: "manageAdminRightsModal",
          isModal: true,
        },
        {
          name: "menuItems.manageAliasModal",
          to: "manageAliasModal",
          isModal: true,
        },
        {
          name: "menuItems.createInternalForwardingAccountModal",
          to: "createInternalForwardingAccountModal",
          isModal: true,
        },
        {
          name: "menuItems.manageUsersModal",
          to: "manageUsersModal",
          isModal: true,
        },
        {
          name: "menuItems.createNewEmailAccountModal",
          to: "createNewEmailAccountModal",
          isModal: true,
        },
        {
          name: "menuItems.resetPasswordModal",
          to: "resetPasswordModal",
          isModal: true,
        },
      ];
    },
  },
  created() {
    this.theme = document.documentElement.getAttribute("theme");
    views.widget["backButtonForNavigation"] = true;
    views.widget["showPoweredByTitan"] = true;
    // Hydrate pending accounts into the store so AppBanner's "add more" nudge
    // can lead its inline examples with them (higher intent). Optional data —
    // the homescreen renders fine without it, so failures only log.
    pendingAccountsService
      .fetchPendingAccounts()
      .catch(e => Logger.warn("cpWidget: pendingAccounts fetch failed", e));
    // First load only: licenses + no mailboxes → lead with the Create
    // email screen instead of the cards homescreen. We don't render Create
    // here; we route to /email_accounts which already auto-opens it on
    // mount. We mark the flag as the redirect fires so this is a one-time
    // lead-in — once the user lands on Create they can navigate home (via
    // the breadcrumb or "Skip and go to control panel") and stay there;
    // we don't keep bouncing them back. A full reload resets the flag, so
    // a fresh session leads with Create again.
    if (this.availableLicenses > 0 && !this.hasMailboxes && !this.skippedFirstMailbox) {
      this.$store.dispatch("mail/mailboxNudge/markFirstMailboxSkipped");
      // Analytics source for the Create screen this lead-in opens.
      smartStorage.setItem("launchEmailCreationSource", "first_account_upfront_creation_screen");
      this.$router.replace({ path: "/email_accounts" });
      return;
    }
    this.$emit("pageLoaded", true);
  },
  methods: {
    handleClickOnHomeScreenTiles(route) {
      // b_tab_clicked — homescreen tiles are the iframe's "tabs". Send the
      // stable analytics `id` (not the i18n `name` key).
      trackTabClicked({ tabName: route.id || route.name });
      if (route.isBillingRoute) {
        this.handleBilling(route.action, route.to.slice(1));
      } else {
        this.$router.push({ path: `${route.to}` });
      }
    },
    handleBilling(action, section) {
      billingService
        .checkPaymentType(action)
        .then(data => this.initiateTransaction(data, section))
        .catch(console.error);
    },
    initiateTransaction(data, section) {
      if (helpers.isAppLoadedInsideIframe() && this.flags.disableIFramePaymentRedirection) {
        helpers.handleRedirectionOnPartnerSide(data.jwt);
        return true;
      }
      if (data.makePaymentUrl !== "inAppPayment") {
        const cpURL = encodeURIComponent(`${window.location.origin}/postPayment`);
        const redirectURL = `${data.makePaymentUrl}?jwt=${data.jwt}&redirect_url=${cpURL}`;
        smartStorage.hostRedirectURL = redirectURL;
        helpers.hardRedirect(redirectURL, true, views.widget["openPaymentURLInDiffTab"]); // skip true, external URL
      } else {
        const partnerId = helpers.getPartnerId();
        const redirectURL = `/partner/cpWidget?jwt=${data.jwt}&partnerId=${partnerId}&section=${section}`;
        helpers.hardRedirect(redirectURL, true, views.widget["openPaymentURLInDiffTab"]);
      }
    },
  },
};
</script>

<style lang="scss">
.widgetParent {
  .widgetHeader {
    display: flex;
    align-items: center;
    .header {
      font-size: 28px;
      display: inline-block;
      padding-right: 22px;
      border-right: 1px solid var(--lineSepaCol);
      margin-right: 17px;
    }
    img {
      width: 80px;
      height: 30px;
    }
  }
  .widgetHeader {
    padding-bottom: 40px;
    .title {
      font-size: 20px;
      font-weight: 600;
    }
    .powered-by {
      width: 92px;
      height: 35px;
      padding-left: 16px;
      margin-left: 16px;
      border-left: 2px solid var(--lineSepaCol);
    }
  }
  .widget-grid-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: var(--widgetHomeTileGap);
    .sections {
      position: relative;
      cursor: pointer;
      height: 205px;
      padding: 15px 20px 28px 20px;
      background: var(--tabBgCol);
      border: 1px solid var(--lightBorderCol);
      overflow: ellipsis;
      a {
        color: var(--primaryTextCol);
        text-decoration: none;
        .widget-section-name {
          text-align: left;
          font-weight: 600;
          font-size: 16px;
          padding-top: 15px;
          color: var(--sectionTitleColor);
          word-break: break-word;
        }
        .widget-section-status {
          margin-top: 4px;
          padding: 4px 8px;
          font-size: 10px;
          font-weight: 600;
          border-radius: 4px;
          text-transform: uppercase;
          color: var(--primaryBtnCol);
          background: var(--readOnlyTxtBgCol);
        }
        .widget-section-description {
          text-align: left;
          padding-top: 3px;
          color: var(--sectionDescriptionColor);
        }
      }
    }
    .commonImgClass {
      background-color: var(--tabBgCol);
    }
    :hover {
      background: var(--hoverTabBgCol);
      .commonImgClass {
        background-color: var(--hoverTabBgCol);
      }
    }
  }
}
.external-tab {
  mask: url("../../assets/externalTabArrow.svg");
  background: var(--iconColor);
  height: 16px;
  width: 16px;
  position: absolute;
  top: 9px;
  right: 20px;
}
</style>
